import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { agentService, apiService, engineService, secretService, teamService } from '../services/api'
import { ArrowLeft, Download, Rocket, Loader2, Key, Plus, Users } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { coreBaseUrl } from '../config/env'

type DeploymentMethod = 'direct' | 'download'

export function DeployAgent() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore()
  
  const [selectedAgentId, setSelectedAgentId] = useState<string>('')

  // * Pre-select agent if provided in URL
  useEffect(() => {
    const agentId = searchParams.get('agentId')
    if (agentId) {
      setSelectedAgentId(agentId)
    }
  }, [searchParams])
  const [deploymentMethod, setDeploymentMethod] = useState<DeploymentMethod>('direct')
  // * La valeur initiale était l'hôte de production écrit en dur : depuis le
  // * Studio de dev, le champ proposait par défaut de déployer l'agent contre
  // * le Core de production.
  const [customCoreUrl, setCustomCoreUrl] = useState<string>(() => `${coreBaseUrl()}/`)
  const [isDeploying, setIsDeploying] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  
  // * Team management
  const [newTeamInstanceName, setNewTeamInstanceName] = useState<string>('')
  const [selectedTeamInstanceId, setSelectedTeamInstanceId] = useState<string>('')
  const [selectedRoleId, setSelectedRoleId] = useState<string>('')
  const [isCreatingTeamInstance, setIsCreatingTeamInstance] = useState(false)
  
  // * API Keys management
  // * Store key IDs (not decrypted values) - keys will be fetched server-side when needed
  // * Structure: { "provider:keyType": keyId } to allow one key per category per provider
  const [selectedApiKeyIds, setSelectedApiKeyIds] = useState<Record<string, string>>({})
  const [moduleChoices, setModuleChoices] = useState<Record<string, string[]>>({})
  const [requirementValues, setRequirementValues] = useState<Record<string, string>>({})
  const [showAddKeyForm, setShowAddKeyForm] = useState(false)
  const [newKey, setNewKey] = useState({ provider: 'anthropic', keyType: 'API_KEY', key: '' })
  
  // * Fetch available API keys
  const { data: apiKeys } = useQuery('apiKeys', apiService.getApiKeys)
  const { data: secrets } = useQuery('secrets', secretService.list)
  const { data: agents, isLoading: agentsLoading } = useQuery('agents', agentService.getAllAgents)
  const selectedModel = agents?.find((item) => item.id === selectedAgentId)
  const selectedEngine = selectedModel?.engineName ?? ''
  const pinnedVersion = selectedModel?.engineVersion ?? ''
  const { data: requirements } = useQuery(
    ['engine-requirements', selectedEngine],
    () => engineService.getRequirements(selectedEngine),
    { enabled: Boolean(selectedEngine), retry: false },
  )
  const requestedModules = selectedEngine
    ? moduleChoices[selectedEngine] ?? requirements?.modules.filter(moduleSpec => moduleSpec.locked || moduleSpec.defaultEnabled).map(moduleSpec => moduleSpec.id) ?? []
    : []
  const { data: selection } = useQuery(
    ['engine-selection', selectedEngine, requirements?.version, requestedModules],
    () => engineService.resolveSelection(selectedEngine, requirements?.version ?? '', requestedModules),
    { enabled: Boolean(selectedEngine && requirements), retry: false },
  )

  // * Fetch team templates
  const { data: teamTemplates } = useQuery('teamTemplates', teamService.getAllTeamTemplates)

  // * Fetch team instances
  const { data: teamInstances } = useQuery('teamInstances', teamService.getAllTeamInstances)

  // * Get selected team instance
  const selectedTeamInstance = teamInstances?.find(t => t.id === selectedTeamInstanceId)
  
  // * Get selected team template from the instance's teamTemplateId
  const selectedTeamTemplate = selectedTeamInstance 
    ? teamTemplates?.find(t => t.id === selectedTeamInstance.teamTemplateId)
    : null

  // * Get available roles for selected team instance
  const availableRoles = selectedTeamInstance && selectedTeamTemplate
    ? selectedTeamTemplate.roles.filter(role => {
        // * Check if role is already occupied (if unique)
        if (role.isUnique) {
          const occupied = selectedTeamInstance.agents?.some(a => a.roleId === role.id)
          return !occupied
        }
        return true
      })
    : []

  function requirementPayload() {
    if (!requirements || !selection) return {}
    return {
      engineVersion: pinnedVersion || requirements.version,
      selectedModules: selection.selectedModules,
      requirementValues,
    }
  }

  function toggleModule(moduleId: string, checked: boolean) {
    const next = checked
      ? [...requestedModules, moduleId]
      : requestedModules.filter(id => id !== moduleId)
    setModuleChoices(current => ({ ...current, [selectedEngine]: next }))
  }

  const handleDirectDeploy = async () => {
    if (!selectedAgentId) {
      alert('Veuillez sélectionner un modèle d\'agent')
      return
    }
    if (!selectedEngine || !pinnedVersion) {
      alert(`Le modèle « ${selectedModel?.name ?? ''} » n'a pas de moteur. Choisissez-le sur le modèle : la version y est figée.`)
      return
    }

    // * Validation: if team is selected, role is required
    if (selectedTeamInstanceId && !selectedRoleId) {
      alert('Veuillez sélectionner un rôle dans l\'équipe')
      return
    }

    setIsDeploying(true)
    console.log('🚀 Démarrage du déploiement...', { agentId: selectedAgentId, engine: selectedEngine })
    
    try {
      console.log('📡 Envoi de la requête de déploiement...')
      // * Send all selected keys in "provider:keyType" format
      // * Backend will map them to the expected config key names
      const apiKeyProviders = Object.keys(selectedApiKeyIds).length > 0 
        ? selectedApiKeyIds
        : undefined

      const response = await agentService.deployAgentInstance(selectedAgentId, {
        engine: selectedEngine,
        engineVersion: pinnedVersion,
        coreUrl: undefined, // * Use default Core URL
        apiKeyIds: requirements ? undefined : apiKeyProviders,
        teamInstanceId: selectedTeamInstanceId || undefined,
        roleId: selectedRoleId || undefined,
        ...requirementPayload(),
      })
      
      console.log('✅ Déploiement réussi:', response)
      alert(`Agent déployé avec succès !\nInstance ID: ${response.instanceId}`)
      navigate('/instances/agents')
    } catch (error: any) {
      console.error('❌ Erreur lors du déploiement:', error)
      console.error('📋 Détails de l\'erreur:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        config: error.config
      })
      
      if (error.response?.status === 504) {
        alert(
          'Le délai du proxy est dépassé (504).\nLe conteneur a peut-être quand même démarré : ouvrez Surveillance avant de relancer.'
        )
        return
      }

      const payloadError = error.response?.data?.error
      const errorMessage = (typeof payloadError === 'object' && payloadError?.message)
                          || (typeof payloadError === 'string' ? payloadError : '')
                          || error.response?.data?.message
                          || error.message
                          || 'Erreur inconnue'
      const errorDetails = error.response?.data?.details ? 
                          `\n\nDétails: ${JSON.stringify(error.response.data.details, null, 2)}` : ''
      
      alert(`Erreur lors du déploiement (${error.response?.status || 'N/A'}):\n${errorMessage}${errorDetails}`)
    } finally {
      setIsDeploying(false)
    }
  }

  const handleDownloadArchive = async () => {
    if (!selectedAgentId) {
      alert('Veuillez sélectionner un modèle d\'agent')
      return
    }
    if (!selectedEngine || !pinnedVersion) {
      alert(`Le modèle « ${selectedModel?.name ?? ''} » n'a pas de moteur. Choisissez-le sur le modèle : la version y est figée.`)
      return
    }

    if (deploymentMethod === 'download' && !customCoreUrl.trim()) {
      alert('Veuillez saisir l\'URL du Core ATF')
      return
    }

      setIsDownloading(true)
    try {
      // * Send all selected keys in "provider:keyType" format
      // * Backend will map them to the expected config key names
      const apiKeyProviders = Object.keys(selectedApiKeyIds).length > 0 
        ? selectedApiKeyIds
        : undefined

      const response = await agentService.downloadAgentDeploymentPackage(selectedAgentId, {
        engine: selectedEngine,
        coreUrl: customCoreUrl.trim() || undefined,
        apiKeyIds: requirements ? undefined : apiKeyProviders,
        ...requirementPayload(),
      })
      
      // * Create download link
      const blob = new Blob([response], { type: 'application/zip' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `agent-deployment-${selectedAgentId}-${Date.now()}.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
      
      alert('Archive téléchargée avec succès !')
    } catch (error: any) {
      console.error('Erreur lors du téléchargement:', error)
      alert(`Erreur lors du téléchargement: ${error.message || 'Erreur inconnue'}`)
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div className="page-content relative min-h-screen">
      {/* Image de robot en arrière-plan */}
      <div className="background-image">
        <img src="/logo.jpg" alt="Robot background" />
      </div>
      
      {/* Contenu au premier plan */}
      <div className="content-wrapper">
        <div className="space-y-6">
          {/* En-tête */}
          <div className="page-toolbar flex items-center space-x-4">
            <button
              onClick={() => navigate('/agents')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Retour</span>
            </button>
            <h1 className="text-3xl font-extrabold tracking-tight tab_title">Déployer un agent</h1>
          </div>

          <div className="card space-y-6">
            <div>
              <h2 className="text-xl font-semibold mb-4 text-primary">Moteur du modèle</h2>
              {!selectedModel && (
                <p className="text-sm text-secondary">Choisissez un modèle. Son moteur et sa version sont déjà fixés.</p>
              )}
              {selectedModel && !selectedEngine && (
                <p className="text-sm text-error">
                  Le modèle « {selectedModel.name} » n'a pas de moteur. Ouvrez-le et choisissez-en un. La version est alors figée.
                </p>
              )}
              {selectedModel && selectedEngine && (
                <p className="text-sm text-primary">
                  Moteur {selectedEngine} {pinnedVersion}. Core refuse l'instanciation si cette version n'est plus disponible.
                </p>
              )}
              {selectedModel && pinnedVersion && requirements && requirements.version !== pinnedVersion && (
                <p className="text-sm text-error mt-2">Moteur {selectedEngine} {pinnedVersion} indisponible</p>
              )}
            </div>

            {requirements && selection && (
              <div>
                <h2 className="text-xl font-semibold mb-4 text-primary">Modules et prérequis</h2>
                <p className="text-sm text-secondary mb-4">
                  Moteur {requirements.engine} {requirements.version}. Une instance déjà lancée garde cette version.
                </p>
                <div className="space-y-3 mb-4">
                  {selection.modules.map(moduleSpec => {
                    const checked = selection.selectedModules.includes(moduleSpec.id)
                    const pulled = selection.pulledBy[moduleSpec.id] ?? []
                    return (
                      <label key={moduleSpec.id} className="flex items-start gap-3 text-primary">
                        <input id={`module-${moduleSpec.id}`} name={`module-${moduleSpec.id}`}
                          type="checkbox"
                          checked={checked}
                          disabled={moduleSpec.locked}
                          onChange={event => toggleModule(moduleSpec.id, event.target.checked)}
                          className="mt-1"
                        />
                        <span>
                          <span className="font-medium">{moduleSpec.id}</span>
                          <span className="block text-sm text-secondary">{moduleSpec.description}</span>
                          {moduleSpec.locked && <span className="block text-sm text-tertiary">Toujours inclus</span>}
                          {pulled.length > 0 && (
                            <span className="block text-sm text-tertiary">Ajouté par {pulled.join(', ')}</span>
                          )}
                        </span>
                      </label>
                    )
                  })}
                </div>
                <div className="space-y-4">
                  {selection.modules
                    .filter(moduleSpec => selection.selectedModules.includes(moduleSpec.id))
                    .flatMap(moduleSpec => moduleSpec.requirements.map(requirement => ({ moduleId: moduleSpec.id, requirement })))
                    .map(({ moduleId, requirement }) => {
                      const matchingSecrets = (secrets ?? []).filter(secret => secret.purpose === requirement.name)
                      return (
                        <div key={requirement.name}>
                          <label className="block text-sm font-medium text-primary mb-1">
                            {requirement.name}
                            {requirement.required ? ' (obligatoire)' : ' (optionnel)'}
                          </label>
                          {requirement.description && (
                            <p className="text-sm text-secondary mb-1">{requirement.description}</p>
                          )}
                          {requirement.type === 'secret' ? (
                            <div className="space-y-2">
                              <select id={`requirement-${requirement.name}`} name={requirement.name}
                                className="form-input w-full"
                                value={requirementValues[requirement.name]?.startsWith('manual:') ? 'manual' : (requirementValues[requirement.name] ?? '')}
                                onChange={event => {
                                  const choice = event.target.value
                                  setRequirementValues(current => {
                                    const next = { ...current }
                                    if (choice === '') delete next[requirement.name]
                                    else if (choice === 'manual') next[requirement.name] = 'manual:'
                                    else next[requirement.name] = choice
                                    return next
                                  })
                                }}
                              >
                                <option value="">Aucun</option>
                                {matchingSecrets.map(secret => (
                                  <option key={secret.id} value={secret.id}>
                                    {secret.keyPreview} · {moduleId}
                                  </option>
                                ))}
                                <option value="manual">Nouvelle valeur</option>
                              </select>
                              {requirementValues[requirement.name]?.startsWith('manual:') && (
                                <input id={`requirement-manual-${requirement.name}`} name={`${requirement.name}-manual`}
                                  type="password"
                                  className="form-input w-full"
                                  autoComplete="off"
                                  value={requirementValues[requirement.name]?.slice('manual:'.length) ?? ''}
                                  onChange={event => setRequirementValues(current => ({
                                    ...current,
                                    [requirement.name]: `manual:${event.target.value}`,
                                  }))}
                                />
                              )}
                            </div>
                          ) : (
                            <input id={`requirement-value-${requirement.name}`} name={requirement.name}
                              type={requirement.type === 'url' ? 'url' : 'text'}
                              className="form-input w-full"
                              value={requirementValues[requirement.name] ?? ''}
                              onChange={event => setRequirementValues(current => ({
                                ...current,
                                [requirement.name]: event.target.value,
                              }))}
                            />
                          )}
                        </div>
                      )
                    })}
                </div>
              </div>
            )}

            {/* Étape 2: Sélection du modèle d'agent */}
            <div>
              <h2 className="text-xl font-semibold mb-4">2. Choisir un modèle d'agent</h2>
              {agentsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : (
                <select id="deploy-agent" name="deploy-agent"
                  value={selectedAgentId}
                  onChange={(e) => setSelectedAgentId(e.target.value)}
                  className="form-input w-full"
                  required
                >
                  <option value="">-- Sélectionner un modèle d'agent --</option>
                  {agents?.map(agent => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name} (v{agent.version})
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Étape 3: Assignation à une équipe (optionnel) */}
            <div>
              <h2 className="text-xl font-semibold mb-4">3. Assigner à une équipe (optionnel)</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Instance d'équipe</label>
                  <select id="deploy-team" name="deploy-team"
                    value={selectedTeamInstanceId}
                    onChange={(e) => {
                      setSelectedTeamInstanceId(e.target.value)
                      setSelectedRoleId('') // * Reset role when team changes
                    }}
                    className="form-input w-full"
                  >
                    <option value="">-- Aucune équipe --</option>
                    {teamInstances?.map(instance => (
                      <option key={instance.id} value={instance.id}>
                        {instance.teamTemplateName} {instance.name ? `- ${instance.name}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedTeamInstanceId && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Rôle dans l'équipe *</label>
                    {availableRoles.length === 0 ? (
                      <p className="text-sm text-secondary">
                        Aucun rôle disponible. Tous les rôles sont peut-être déjà occupés.
                      </p>
                    ) : (
                      <select id="deploy-role" name="deploy-role"
                        value={selectedRoleId}
                        onChange={(e) => setSelectedRoleId(e.target.value)}
                        className="form-input w-full"
                        required
                      >
                        <option value="">-- Sélectionner un rôle --</option>
                        {availableRoles.map(role => (
                          <option key={role.id} value={role.id}>
                            {role.name} {role.isUnique && '(Unique)'}
                          </option>
                        ))}
                      </select>
                    )}
                    <p className="text-xs text-secondary mt-2">
                      Les compétences se déclarent sur le modèle. S'il en manque une pour ce rôle, l'instanciation est refusée.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Étape 4: Clés API — repli si le moteur n'a pas de requirements.json */}
            {!requirements && <div>
              <h2 className="text-xl font-semibold mb-4">4. Clés API</h2>
              <p className="text-sm text-gray-600 mb-4">
                Sélectionnez ou ajoutez des clés API pour les fournisseurs LLM. Ces clés seront incluses dans la configuration de l'agent.
                Les clés enregistrées ne peuvent pas être affichées en clair pour des raisons de sécurité.
              </p>
              
              {/* Liste des clés disponibles */}
              {apiKeys && apiKeys.length > 0 && (
                <div className="space-y-4 mb-4">
                  <label className="block text-sm font-medium text-primary mb-2">Clés enregistrées</label>
                  {Array.from(new Set(apiKeys.map(k => k.provider))).map((provider) => {
                    const providerKeys = apiKeys.filter(k => k.provider === provider)
                    // * Group keys by keyType
                    const keysByType = providerKeys.reduce((acc, key) => {
                      if (!acc[key.keyType]) {
                        acc[key.keyType] = []
                      }
                      acc[key.keyType].push(key)
                      return acc
                    }, {} as Record<string, typeof providerKeys>)
                    
                    return (
                      <div key={provider} className="border border-gray-200 rounded-lg p-3 space-y-3">
                        <div className="font-medium text-sm text-primary">{provider}</div>
                        {Object.entries(keysByType).map(([keyType, keys]) => {
                          const compositeKey = `${provider}:${keyType}`
                          const selectedKeyId = selectedApiKeyIds[compositeKey]
                          return (
                            <div key={keyType} className="pl-4 border-l-2 border-gray-200">
                              <div className="text-xs font-medium text-secondary mb-2">{keyType}</div>
                              <div className="flex flex-wrap items-center gap-2">
                                <label className="flex items-center gap-2">
                                  <input
                                    type="radio"
                                    name={`apiKey_${compositeKey}`}
                                    value=""
                                    checked={!selectedKeyId}
                                    onChange={() => {
                                      const newKeys = { ...selectedApiKeyIds }
                                      delete newKeys[compositeKey]
                                      setSelectedApiKeyIds(newKeys)
                                    }}
                                    className="mr-2"
                                  />
                                  <span className="text-xs text-gray-500">Aucune</span>
                                </label>
                                {keys.map((key) => (
                                  <label key={key.id} className="flex items-center gap-2">
                                    <input
                                      type="radio"
                                      name={`apiKey_${compositeKey}`}
                                      value={key.id}
                                      checked={selectedKeyId === key.id}
                                      onChange={() => {
                                        setSelectedApiKeyIds({ ...selectedApiKeyIds, [compositeKey]: key.id })
                                      }}
                                      className="mr-2"
                                    />
                                    <span className="text-sm">{key.keyPreview}</span>
                                    {key.description && (
                                      <span className="text-xs text-gray-500">({key.description})</span>
                                    )}
                                  </label>
                                ))}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Formulaire d'ajout rapide */}
              {showAddKeyForm ? (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-1">Fournisseur</label>
                    <select id="deploy-key-provider" name="deploy-key-provider"
                      value={newKey.provider}
                      onChange={(e) => setNewKey({ ...newKey, provider: e.target.value })}
                      className="input"
                    >
                      <option value="mistral">Mistral AI</option>
                      <option value="anthropic">Anthropic (Claude)</option>
                      <option value="openai">OpenAI</option>
                      <option value="assembly">Assembly.ai</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-1">Type de clé</label>
                    <select id="deploy-key-type" name="deploy-key-type"
                      value={newKey.keyType}
                      onChange={(e) => setNewKey({ ...newKey, keyType: e.target.value })}
                      className="input"
                    >
                      <option value="API_KEY">Clé API</option>
                      <option value="SECRET">Secret</option>
                      <option value="TOKEN">Token</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-primary mb-1">Clé API</label>
                    <input id="deploy-key-value" name="deploy-key-value"
                      type="password"
                      value={newKey.key}
                      onChange={(e) => setNewKey({ ...newKey, key: e.target.value })}
                      placeholder="sk-..."
                      className="input w-full"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={async () => {
                        if (!newKey.key) {
                          alert('Veuillez saisir une clé')
                          return
                        }
                        // * For manual keys, we'll send the key directly (temporary, not stored)
                        const compositeKey = `${newKey.provider}:${newKey.keyType}`
                        setSelectedApiKeyIds({ ...selectedApiKeyIds, [compositeKey]: `manual:${newKey.key}` })
                        setNewKey({ provider: 'anthropic', keyType: 'API_KEY', key: '' })
                        setShowAddKeyForm(false)
                      }}
                      className="button button--primary text-sm"
                    >
                      Utiliser cette clé
                    </button>
                    <button
                      onClick={() => {
                        setShowAddKeyForm(false)
                        setNewKey({ provider: 'anthropic', keyType: 'API_KEY', key: '' })
                      }}
                      className="button button--secondary text-sm"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowAddKeyForm(true)}
                  className="button button--secondary flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Ajouter une clé manuellement</span>
                </button>
              )}

              {/* Clés sélectionnées */}
              {Object.keys(selectedApiKeyIds).length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="text-sm font-medium text-primary mb-2">Clés sélectionnées :</div>
                  <div className="space-y-1">
                    {Object.entries(selectedApiKeyIds).map(([compositeKey, keyId]) => {
                      const [provider, keyType] = compositeKey.split(':')
                      const key = apiKeys?.find(k => k.id === keyId)
                      const isManual = keyId.startsWith('manual:')
                      return (
                        <div key={compositeKey} className="text-sm text-secondary flex items-center gap-2">
                          <Key className="w-4 h-4" />
                          <span className="font-medium">{provider}</span>
                          <span className="text-xs text-gray-500">({keyType}):</span>
                          {isManual ? (
                            <span className="font-mono text-xs">Clé manuelle (non stockée)</span>
                          ) : (
                            <span className="font-mono">{key?.keyPreview || 'Clé sélectionnée'}</span>
                          )}
                          <button
                            onClick={() => {
                              const newKeys = { ...selectedApiKeyIds }
                              delete newKeys[compositeKey]
                              setSelectedApiKeyIds(newKeys)
                            }}
                            className="text-red-600 hover:text-red-800 ml-2"
                          >
                            ×
                          </button>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>}

            {/* Étape 4: Méthode de déploiement */}
            <div>
              <h2 className="text-xl font-semibold mb-4">4. Choisir la méthode de déploiement</h2>
              <div className="space-y-4">
                {/* Option 1: Déploiement direct */}
                <label
                  className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    deploymentMethod === 'direct'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="deploymentMethod"
                    value="direct"
                    checked={deploymentMethod === 'direct'}
                    onChange={(e) => setDeploymentMethod(e.target.value as DeploymentMethod)}
                    className="mt-1 mr-3"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <Rocket className="w-5 h-5 text-blue-600" />
                      <div className="font-medium">Déploiement direct</div>
                    </div>
                    <div className="text-sm text-gray-600">
                      Lance une instance de l'agent directement dans le Core ATF disponible sur ce serveur.
                      L'agent s'enregistrera automatiquement et sera accessible via les routes Traefik.
                    </div>
                  </div>
                </label>

                {/* Option 2: Téléchargement d'archive */}
                <label
                  className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    deploymentMethod === 'download'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="deploymentMethod"
                    value="download"
                    checked={deploymentMethod === 'download'}
                    onChange={(e) => setDeploymentMethod(e.target.value as DeploymentMethod)}
                    className="mt-1 mr-3"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <Download className="w-5 h-5 text-blue-600" />
                      <div className="font-medium">Télécharger une archive de déploiement</div>
                    </div>
                    <div className="text-sm text-gray-600 mb-3">
                      Télécharge une archive ZIP contenant le moteur complet, les fichiers de configuration
                      et les scripts d'installation pour déployer l'agent sur un autre serveur.
                    </div>
                    {deploymentMethod === 'download' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          URL du Core ATF (pour déploiement sur un autre serveur)
                        </label>
                        <input id="deploy-core-url" name="deploy-core-url"
                          type="url"
                          value={customCoreUrl}
                          onChange={(e) => setCustomCoreUrl(e.target.value)}
                          placeholder="https://core.atf.example.com"
                          className="form-input w-full"
                        />
                        <div className="text-xs text-gray-500 mt-1">
                          Si vide, l'URL devra être configurée manuellement dans les fichiers de configuration.
                        </div>
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-4 pt-4 border-t">
              <button
                type="button"
                onClick={() => navigate('/agents')}
                className="button button--secondary"
              >
                Annuler
              </button>
              {deploymentMethod === 'direct' ? (
                <button
                  type="button"
                  onClick={handleDirectDeploy}
                  disabled={!selectedAgentId || isDeploying}
                  className="button button--primary flex items-center space-x-2"
                >
                  {isDeploying ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Déploiement en cours...</span>
                    </>
                  ) : (
                    <>
                      <Rocket className="w-5 h-5" />
                      <span>Déployer maintenant</span>
                    </>
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleDownloadArchive}
                  disabled={!selectedAgentId || isDownloading}
                  className="button button--primary flex items-center space-x-2"
                >
                  {isDownloading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Téléchargement en cours...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-5 h-5" />
                      <span>Télécharger l'archive</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

